import React, { useState, useEffect, useRef } from 'react';
import './LogViewer.css';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : 'http://localhost:3001/api';

interface LogViewerProps {
  executionId: string;
  isPendingApproval?: boolean; // Stop auto-scroll and polling when waiting for approval
}

interface LogEntry {
  timestamp: string;
  message: string;
  type: 'info' | 'error' | 'success' | 'status';
}

const LogViewer: React.FC<LogViewerProps> = ({ executionId, isPendingApproval = false }) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [connected, setConnected] = useState(false);
  const [workflowCompleted, setWorkflowCompleted] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const lastStatusRef = useRef<string | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const currentExecutionIdRef = useRef<string>(executionId);

  // Clear logs when executionId changes (switching to a different workflow)
  useEffect(() => {
    if (currentExecutionIdRef.current !== executionId) {
      console.log(`🔄 Execution changed from ${currentExecutionIdRef.current} to ${executionId} - clearing logs`);
      setLogs([]);
      setConnected(false);
      setWorkflowCompleted(false);
      lastStatusRef.current = null;
      reconnectAttemptsRef.current = 0;
      currentExecutionIdRef.current = executionId;

      // Close any existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    }
  }, [executionId]);

  useEffect(() => {
    // Don't connect if waiting for approval
    if (isPendingApproval) {
      console.log('⏸️  LogViewer: Skipping log streaming - waiting for approval');
      setConnected(false);
      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      return;
    }

    const connectToStream = () => {
      // Clean up existing connection
      if (eventSourceRef.current) {
        console.log('🧹 Closing existing EventSource connection');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      // Get auth token from localStorage
      const accessToken = localStorage.getItem('accessToken');

      // Connect to SSE endpoint with token as query parameter
      const url = new URL(`${API_BASE_URL}/executions/${executionId}/logs/stream`);
      if (accessToken) {
        url.searchParams.append('token', accessToken);
      }

      console.log(`🔌 Connecting to log stream for execution: ${executionId}`);
      const eventSource = new EventSource(url.toString());
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log('✅ Log stream connected');
        setConnected(true);
        reconnectAttemptsRef.current = 0; // Reset reconnection counter
        addLog('Connected to log stream', 'info');
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'connected') {
            addLog(data.message, 'info');
          } else if (data.type === 'status') {
            // Only log status if it changed
            if (lastStatusRef.current !== data.status) {
              addLog(`Workflow status: ${data.status}`, 'status');
              lastStatusRef.current = data.status;
            }
          } else if (data.type === 'terminal') {
            // Workflow reached terminal state - stop streaming
            addLog(`🏁 ${data.message}`, 'status');
            setWorkflowCompleted(true);
            setConnected(false);
            eventSource.close();
          } else if (data.type === 'log') {
            // Parse log line to determine type
            const message = data.message;
            let logType: LogEntry['type'] = 'info';

            // Only mark as error if it's an actual error message
            // Don't mark stderr output as error (it often contains normal output)
            const lowerMessage = message.toLowerCase();
            if (
              lowerMessage.includes('failed') ||
              lowerMessage.includes('exception') ||
              lowerMessage.includes('fatal') ||
              (lowerMessage.includes('error') && (
                lowerMessage.includes('error:') ||
                lowerMessage.includes('error occurred') ||
                lowerMessage.includes('error executing') ||
                lowerMessage.includes('error creating')
              ))
            ) {
              logType = 'error';
            } else if (lowerMessage.includes('success') || message.includes('✓') || message.includes('✅')) {
              logType = 'success';
            }

            addLog(message, logType);
          } else {
            addLog(data.message || JSON.stringify(data), 'info');
          }
        } catch (err) {
          console.error('Error parsing log event:', err);
        }
      };

      eventSource.onerror = (error) => {
        console.error('❌ SSE connection error:', error);
        setConnected(false);

        // Close the failed connection
        eventSource.close();

        // Clear the reference
        if (eventSourceRef.current === eventSource) {
          eventSourceRef.current = null;
        }

        // Don't reconnect if execution changed (will be handled by useEffect)
        if (currentExecutionIdRef.current !== executionId) {
          console.log('⏭️  Execution changed, skipping reconnection');
          return;
        }

        // Don't reconnect if pending approval (workflow is paused)
        if (isPendingApproval) {
          console.log('⏸️  Workflow pending approval, skipping reconnection');
          return;
        }

        // Implement exponential backoff reconnection with shorter max attempts
        const maxAttempts = 5;
        if (reconnectAttemptsRef.current < maxAttempts) {
          reconnectAttemptsRef.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 10000); // Max 10 seconds

          addLog(`⚠️ Connection lost. Reconnecting in ${delay / 1000}s... (attempt ${reconnectAttemptsRef.current}/${maxAttempts})`, 'status');

          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`🔄 Attempting reconnection (${reconnectAttemptsRef.current}/${maxAttempts})...`);
            connectToStream();
          }, delay);
        } else {
          addLog('❌ Connection failed after multiple attempts. The workflow may have completed or logs may be unavailable.', 'error');
        }
      };
    };

    // Initial connection
    connectToStream();

    return () => {
      console.log('🧹 Cleaning up LogViewer connection');
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [executionId, isPendingApproval]);

  const addLog = (message: string, type: LogEntry['type'] = 'info') => {
    const newLog: LogEntry = {
      timestamp: new Date().toISOString(),
      message,
      type
    };
    setLogs(prev => [...prev, newLog]);
  };

  // Auto-scroll to bottom only if user is already at the bottom AND not waiting for approval
  useEffect(() => {
    if (!isPendingApproval && autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll, isPendingApproval]);

  // Detect if user has scrolled away from bottom
  const handleScroll = () => {
    if (logContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50; // 50px threshold
      setAutoScroll(isAtBottom);
    }
  };

  const getLogClass = (type: string) => {
    switch (type) {
      case 'error': return 'log-error';
      case 'success': return 'log-success';
      case 'status': return 'log-status';
      default: return 'log-info';
    }
  };

  // Determine connection status display
  const getConnectionStatus = () => {
    if (workflowCompleted) {
      return { class: 'completed', text: '✅ Completed' };
    }
    if (isPendingApproval) {
      return { class: 'paused', text: '⏸️ Paused' };
    }
    if (connected) {
      return { class: 'connected', text: '🟢 Live' };
    }
    return { class: 'disconnected', text: '⚠️ Disconnected' };
  };

  const connectionStatus = getConnectionStatus();

  return (
    <div className="log-viewer">
      <div className="log-header">
        <h4>📋 Execution Logs</h4>
        <span className={`connection-status ${connectionStatus.class}`}>
          {connectionStatus.text}
        </span>
      </div>
      <div className="log-content" ref={logContainerRef} onScroll={handleScroll}>
        {logs.length === 0 ? (
          <div className="log-empty">Waiting for logs...</div>
        ) : (
          logs.map((log, index) => (
            <div key={index} className={`log-entry ${getLogClass(log.type)}`}>
              <span className="log-timestamp">
                {new Date(log.timestamp).toLocaleTimeString()}
              </span>
              <pre className="log-message">{log.message}</pre>
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
};

export default LogViewer;

